import { inject, Injectable } from '@angular/core';
import {
  BdCompanyContextService,
  BdGrantsService,
  ShareableGrantFull,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import { map, Observable, of, shareReplay } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * The grants the signed-in user holds, bucketed by the group they were granted
 * to. Grants that reached the user personally (a colleague shared with them
 * directly) have no group bucket and are deliberately absent — a group tree can
 * only show what arrived through that group.
 */
export interface GroupShareIndex {
  /** Group id → its grants, one row per shared resource. */
  readonly byGroupId: ReadonlyMap<string, readonly ShareableGrantFull[]>;
}

const EMPTY_INDEX: GroupShareIndex = { byGroupId: new Map() };

/**
 * One source of truth for "what has been shared with which group", behind a
 * single `myGrants` request per company context.
 *
 * Both the scope sidebar (which badges every group node with its share count)
 * and the group "Geteilt" tab read from here. They mount independently and
 * would otherwise fire the same unpaginated grants request twice — `shareReplay`
 * on the cached observable dedupes even while the first one is still in flight,
 * which the codebase's `cacheObservable` does not.
 */
@Injectable({ providedIn: 'root' })
export class BdGroupSharesService {
  private readonly _grantsService = inject(BdGrantsService);
  private readonly _companyContext = inject(BdCompanyContextService);

  /** Keyed by company context — "Alle Kunden" (no id) is its own entry. */
  private readonly _indexByCompany = new Map<
    string,
    Observable<GroupShareIndex>
  >();

  /**
   * The index for the active company context. Replays to late subscribers, so
   * the sidebar and the tab share one response.
   */
  public index(): Observable<GroupShareIndex> {
    const companyId = this._companyContext.currentCompanyId;
    const key = companyId ?? '';
    const cached = this._indexByCompany.get(key);
    if (cached) {
      return cached;
    }
    const request = this._grantsService.myGrants('READ', companyId).pipe(
      map((response) => this._toIndex(response)),
      catchError(() => of(EMPTY_INDEX)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this._indexByCompany.set(key, request);
    return request;
  }

  /** Grants that reached the user through `groupId`, in API order. */
  public grantsOf(groupId: string): Observable<readonly ShareableGrantFull[]> {
    return this.index().pipe(map((idx) => idx.byGroupId.get(groupId) ?? []));
  }

  /**
   * Drops every cached response. Called after an action that changes what is
   * shared (revoking a grant), so the next read reflects it.
   */
  public invalidate(): void {
    this._indexByCompany.clear();
  }

  private _toIndex(
    response: ShareableGrantFull[] | { content: ShareableGrantFull[] },
  ): GroupShareIndex {
    const grants: ShareableGrantFull[] = Array.isArray(response)
      ? response
      : (response.content ?? []);
    const byGroupId = new Map<string, ShareableGrantFull[]>();
    const seenPerGroup = new Map<string, Set<string>>();
    grants.forEach((grant) => {
      if (grant.grantee?.type !== 'group' || !grant.grantee.id) {
        return;
      }
      const groupId = grant.grantee.id;
      const resourceId = grant.shareableReference?.id;
      const seen = seenPerGroup.get(groupId) ?? new Set<string>();
      if (!resourceId || seen.has(resourceId)) {
        return;
      }
      // A resource can reach the user through more than one grant on the same
      // group (different permission levels) — keep the first, exactly as the
      // global "Geteilt" list does (BDA-5971), so it is listed once.
      seen.add(resourceId);
      seenPerGroup.set(groupId, seen);
      const bucket = byGroupId.get(groupId) ?? [];
      bucket.push(grant);
      byGroupId.set(groupId, bucket);
    });
    return { byGroupId };
  }
}
